import styled from "styled-components";
import { TableCell, TableRow, Paper, TableContainer, Box } from "@mui/material";
import { colors, stylesGlobalDefault, globalStyle } from "global/globalStyle";

export const StyledPaper = styled(Paper)`
  && {
    width: 100%;
    overflow: hidden;
    box-shadow: none;
    border-radius: 0;
    margin-top: 20px;
    background: transparent;
    border: none;
  }

  margin-bottom: 2px;
`;

export const StyledTableContainer = styled(TableContainer)<{ maxHeight?: string }>`
  && {
    max-height: ${(props) => props.maxHeight || "60vh"};
  }
`;

export const StyledTableCell = styled(TableCell)`
  && {
    background-color: ${colors.neutral_gray_70};
    color: ${colors.neutral_white};
    font-weight: 700;
    font-size: 14px;
    font-family: "Inter", sans-serif;
    padding: 20px;
    border-bottom: none;
    border: none;
  }
`;

export const StyledDataTableCell = styled(TableCell)`
  && {
    font-size: 14px;
    font-weight: 700;
    font-family: "Inter", sans-serif;
    padding: 0 20px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    border: none;
    height: 60px;
  }
`;

export const StyledTableRow = styled(TableRow)<{ striped?: boolean }>`
  && {
    background-color: ${colors.background_lighter};
    border-radius: 8px;
    border: 2px solid ${colors.background_lighter};
    display: table-row;
    margin-bottom: 10px;

    &:hover {
      border: 2px solid ${colors.primary_default};
      cursor: default;
    }

    &:not(:last-child) {
      td {
        padding-bottom: 10px;
      }
    }

    &:last-child td,
    &:last-child th {
      border: 0;
    }

    td:first-child {
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
    }

    td:last-child {
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
    }
  }
`;

export const LoadingContainer = styled(Box)`
  && {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
  }
`;

export const EmptyContainer = styled(Box)`
  && {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
    flex-direction: column;
  }
`;

export const EllipsisCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
`;

export const EllipsisText = styled.div<{ maxWidth?: string }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  max-width: ${(props) => props.maxWidth || "200px"};
`;

export const ViewMoreButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
  color: ${colors.general_icons};
  opacity: 0.6;

  &:hover {
    opacity: 1;
    color: ${colors.primary_default};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const TableWrapper = styled.div`
  width: 100%;
  margin-top: 20px;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  height: 64px;
  background-color: #D1DBE3;
  border: 2px solid #D1DBE3;
  border-radius: 8px;
  margin-bottom: 10px;
  padding: 0 20px;
`;

export const HeaderCell = styled.div<{ width?: string; align?: string }>`
  flex: ${(props) => (props.width ? "none" : 1)};
  width: ${(props) => props.width || "auto"};
  text-align: ${(props) => props.align || "left"};
  font-size: 12px;
  font-weight: 600;
  color: ${colors.neutral_black};
  font-family: "Inter", sans-serif;
`;

export const CardsContainer = styled.div<{ maxHeight?: string }>`
  max-height: ${(props) => props.maxHeight || "60vh"};
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.background_light};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.neutral_gray_70};
    border-radius: 4px;
  }
`;

export const CardRow = styled.div`
  display: flex;
  align-items: center;
  height: 66px;
  background-color: #ECF0F3;
  border: 2px solid #ECF0F3;
  border-radius: 8px;
  margin-bottom: 10px;
  padding: 0 20px;
  cursor: default;
  transition: border 0.2s;

  &:hover {
    border: 2px solid ${colors.primary_default};
  }
`;

export const CardCell = styled.div<{ width?: string; align?: string }>`
  flex: ${(props) => (props.width ? "none" : 1)};
  width: ${(props) => props.width || "auto"};
  text-align: ${(props) => props.align || "left"};
  font-size: 12px;
  font-weight: 400;
  color: ${colors.neutral_black};
  font-family: "Inter", sans-serif;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.4;
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 6vh;
  margin-bottom: 14px;
  padding-right: 20px;
  padding-top: 14px;

  .pagination {
    display: flex;
    justify-content: end;
    list-style: none;
    cursor: pointer;
  }

  .pagination a {
    width: 40px;
    height: 40px;
    border-radius: 10%;
    margin: 2px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: none;
  }

  .pagination__link {
    font-weight: bold;
    color: #1d9bf0;
    background-color: none !important;
    text-decoration: none;
    padding: 0px !important;
    border-radius: 10% !important;
    border: 0px #ffffff !important;
    width: 40px !important;
  }

  .pagination__link:hover {
    background-color: #ffffff;
    color: #3e424e;
  }

  .pagination__link--active a {
    color: #ffffff !important;
    background: #1d9bf0;
  }

  .pagination__link--disabled a {
    color: #1d9bf0;
    background-color: none !important;
    padding: 0px !important;
    border-radius: 10% !important;
    width: 40px !important;
  }

  .previusAndNextDisableCursor {
    cursor: default;
  }

  .previusAndNextDisableCursor a {
    cursor: default;
  }
`;
